import { useCallback, useEffect, useRef, useState } from 'react';
import { Minion, Tower, JungleCamp, SimulationState } from './simulationTypes';
import { createInitialState, tickSimulation } from './minionLogic';

/* ══════════════════ Display-only snapshot (cheap to diff in React) ══════════════════ */

export interface SimDisplayState {
  gameTime: number;
  running: boolean;
  speed: number;
  blueAlive: number;
  redAlive: number;
  waveBlue: number;
  waveRed: number;
  blueTowersAlive: number;
  redTowersAlive: number;
}

function snapshot(s: SimulationState): SimDisplayState {
  let blue = 0;
  let red = 0;
  for (const m of s.minions) {
    if (m.team === 'blue') blue++;
    else red++;
  }
  let blueTowers = 0;
  let redTowers = 0;
  for (const t of s.towers) {
    if (!t.alive) continue;
    if (t.team === 'blue') blueTowers++;
    else redTowers++;
  }
  return {
    gameTime: s.gameTime,
    running: s.running,
    speed: s.speed,
    blueAlive: blue,
    redAlive: red,
    waveBlue: s.waveCounter.blue,
    waveRed: s.waveCounter.red,
    blueTowersAlive: blueTowers,
    redTowersAlive: redTowers,
  };
}

/* ══════════════════ Hook ══════════════════ */

export interface SimulationControls {
  /** Current display snapshot (updated at ~10 fps for UI) */
  display: SimDisplayState;
  /** Read-only reference to the live minion array (for canvas rendering) */
  minionsRef: React.MutableRefObject<Minion[]>;
  /** Read-only reference to jungle camps */
  campsRef: React.MutableRefObject<JungleCamp[]>;
  /** Direct reference to the SimulationState (for jungle camp interactions etc.) */
  simRef: React.MutableRefObject<SimulationState>;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  reset: () => void;
  setSpeed: (s: number) => void;
  /** Force a display-state refresh (useful after mutating sim directly, e.g. camp kills) */
  refreshDisplay: () => void;
  /** Run the simulation forward by `seconds` of game-time synchronously, then refresh UI. */
  fastForward: (seconds: number) => void;
}

export function useSimulation(
  /** Called each animation frame with the current minion + tower arrays + sim delta for canvas rendering */
  onFrame?: (minions: Minion[], towers: Tower[], simDt: number) => void,
): SimulationControls {
  /* Keep the full simulation state in a ref for performance (no React re-render per frame). */
  const simRef = useRef<SimulationState>(createInitialState());
  const minionsRef = useRef<Minion[]>(simRef.current.minions);
  const campsRef = useRef<JungleCamp[]>(simRef.current.jungleCamps);

  /* A lightweight React state for the UI (updated at throttled rate). */
  const [display, setDisplay] = useState<SimDisplayState>(snapshot(simRef.current));

  /* Animation frame bookkeeping */
  const rafRef = useRef<number>(0);
  const lastTsRef = useRef<number>(0);
  const lastUiRef = useRef<number>(0);

  /* ── Frame loop ── */
  const loop = useCallback(
    (timestamp: number) => {
      const sim = simRef.current;
      if (!sim.running) return;

      if (lastTsRef.current === 0) {
        lastTsRef.current = timestamp;
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const realDt = Math.min((timestamp - lastTsRef.current) / 1000, 0.1); // cap to avoid spiral
      lastTsRef.current = timestamp;
      const simDt = realDt * sim.speed;

      // Mutate sim state in-place
      tickSimulation(sim, simDt);
      minionsRef.current = sim.minions;
      campsRef.current = sim.jungleCamps;

      // Notify host for canvas drawing + champion movement
      onFrame?.(sim.minions, sim.towers, simDt);

      // Throttled UI update (~10 fps)
      if (timestamp - lastUiRef.current > 100) {
        setDisplay(snapshot(sim));
        lastUiRef.current = timestamp;
      }

      rafRef.current = requestAnimationFrame(loop);
    },
    [onFrame],
  );

  /* Start / stop the loop when running flag changes */
  const startLoop = useCallback(() => {
    lastTsRef.current = 0;
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  /* ── Controls ── */
  const play = useCallback(() => {
    simRef.current.running = true;
    setDisplay((d) => ({ ...d, running: true }));
    startLoop();
  }, [startLoop]);

  const pause = useCallback(() => {
    simRef.current.running = false;
    cancelAnimationFrame(rafRef.current);
    setDisplay((d) => ({ ...d, running: false }));
  }, []);

  const togglePlay = useCallback(() => {
    if (simRef.current.running) pause();
    else play();
  }, [play, pause]);

  const reset = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    simRef.current = createInitialState();
    minionsRef.current = simRef.current.minions;
    campsRef.current = simRef.current.jungleCamps;
    setDisplay(snapshot(simRef.current));
  }, []);

  const setSpeed = useCallback((s: number) => {
    simRef.current.speed = s;
    setDisplay((d) => ({ ...d, speed: s }));
  }, []);

  const refreshDisplay = useCallback(() => {
    setDisplay(snapshot(simRef.current));
  }, []);

  const fastForward = useCallback(
    (seconds: number) => {
      const sim = simRef.current;
      const step = 0.05; // 50 ms sim-time per tick
      const steps = Math.ceil(seconds / step);
      for (let i = 0; i < steps; i++) {
        tickSimulation(sim, step);
      }
      minionsRef.current = sim.minions;
      campsRef.current = sim.jungleCamps;
      onFrame?.(sim.minions, sim.towers, 0);
      setDisplay(snapshot(sim));
    },
    [onFrame],
  );

  return { display, minionsRef, campsRef, simRef, play, pause, togglePlay, reset, setSpeed, refreshDisplay, fastForward };
}
